import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { ReactElement } from 'react';
import useGetSiteControlUserParams from 'services/hooks/getSiteControl/useGetSiteControlUserParams';
import {
  useAuthStore,
  useDidMountEffect,
  useSongsStore,
  useTagsStore,
} from 'services/hooks';
import { useAnalyticsTracking } from 'services/hooks/funnel';
import type { RootState } from 'services/store';
import { analytics, analyticsListener, events } from 'services/analytics';
import useGetSiteControlScript from 'services/hooks/getSiteControl/useGetSiteControlScript';
import PageLoading from '../PageLoading';

type Props = { children: ReactElement };

const StartUp = ({ children }: Props): ReactElement => {
  const firstRender = useRef(true);

  const { tagsStore } = useTagsStore();
  const { getSongs, resetSongs } = useSongsStore();
  const { authStore, loginToApp, isLoginToAppLoading } = useAuthStore();
  const { isLoggedIn } = authStore;
  const { email } = useSelector((store: RootState) => store.user);

  useGetSiteControlScript();
  useGetSiteControlUserParams();

  // initial login to app
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      if (!isLoggedIn) {
        loginToApp();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    analytics.track(events.profitWellLogin(email));
  }, [email]);

  const resetSongList = () => {
    resetSongs();
    const params = {
      page: 1,
      selectedTags: tagsStore.selectedTags,
      appliedFilters: tagsStore.appliedFilters,
    };
    getSongs(params);
  };

  // handle getting songs for not logged in users
  useDidMountEffect(() => {
    if (!isLoginToAppLoading && !isLoggedIn) {
      resetSongList();
    }
  }, [isLoginToAppLoading]);

  // getting songs for logged in users (triggered after initial login)
  useDidMountEffect(() => {
    resetSongList();
  }, [isLoggedIn, tagsStore.selectedTags, tagsStore.appliedFilters]);

  const { trackUserStatus } = useAnalyticsTracking();

  analyticsListener.register(() => {
    // track the user status on the first run of the app if the user has given his consent
    trackUserStatus();
  });

  return firstRender.current || isLoginToAppLoading ? (
    <PageLoading />
  ) : (
    children
  );
};

export default StartUp;
